<template>
  <div class="feedback-view">
    <Loader :loading="showLoader" />

    <!-- Modern Header -->
    <v-sheet color="transparent" class="header-section">
      <v-card rounded="xl" elevation="6" class="header-card px-4 px-md-6 py-3 py-md-4">
        <div class="d-flex align-center">
          <v-btn
            icon
            variant="text"
            size="small"
            class="back-btn mr-3"
            @click="goBack"
          >
            <v-icon color="white" size="28">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="logo-wrapper mr-3">
            <img :src="logo" class="toolbar-logo" alt="Logo" />
          </div>
          <div class="header-title">{{ $t('feedback-form.form') }}</div>
        </div>
      </v-card>
    </v-sheet>

    <!-- User Feedback Form -->
    <v-container v-if="!showLoader" fluid class="content-container">
      <v-row>
        <!-- Left Column - Form Header -->
        <v-col cols="12" md="4" class="d-flex flex-column">
          <v-card rounded="xl" elevation="3" class="info-card pa-5 pa-md-6 flex-grow-1">
            <div class="feedback-icon-wrapper mb-4">
              <v-icon size="56" color="#285096">mdi-message-text-outline</v-icon>
            </div>
            <h2 class="info-title mb-3">{{ $t('feedback-form.submit-feeback') }}</h2>
            <p class="info-description">{{ $t('feedback-form.feedback-description') }}</p>
          </v-card>
        </v-col>

        <!-- Right Column - Form Fields -->
        <v-col cols="12" md="8">
          <v-card rounded="xl" elevation="3" class="feedback-card pa-5 pa-md-6">
            <v-form class="feedback-form">
              <v-row dense>
                <!-- Category and Rating Row -->
                <v-col cols="12" sm="6">
                  <div class="form-group">
                    <label class="form-label">
                      <v-icon size="18" class="mr-2" color="#285096">mdi-folder-outline</v-icon>
                      {{ $t('feedback-form.select-category') }}
                    </label>
                    <v-select
                      v-model="feedback.category"
                      :items="categories"
                      variant="outlined"
                      rounded="lg"
                      density="comfortable"
                      :menu-props="{ maxHeight: '300px' }"
                      class="form-select"
                      required
                    ></v-select>
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="form-group">
                    <label class="form-label">
                      <v-icon size="18" class="mr-2" color="#285096">mdi-star-outline</v-icon>
                      {{ $t('feedback-form.rate-application') }}
                    </label>
                    <div class="rating-wrapper">
                      <v-rating
                        v-model="feedback.rating"
                        color="#F39C12"
                        active-color="#F39C12"
                        size="large"
                        hover
                        class="star-rating"
                      ></v-rating>
                    </div>
                  </div>
                </v-col>

                <!-- Name Field and Checkbox Row -->
                <v-col cols="12" sm="6">
                  <div class="form-group">
                    <label class="form-label">
                      <v-icon size="18" class="mr-2" color="#285096">mdi-account-outline</v-icon>
                      {{ $t('feedback-form.your-name') }}
                    </label>
                    <v-text-field
                      v-model="feedback.name"
                      variant="outlined"
                      rounded="lg"
                      density="comfortable"
                      :disabled="isAnonymous"
                      class="form-input"
                    ></v-text-field>
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <div class="form-group checkbox-group">
                    <label class="form-label invisible">Checkbox</label>
                    <v-checkbox
                      v-model="isAnonymous"
                      color="#285096"
                      @change="toggleAnonymous"
                      class="anonymous-checkbox"
                      hide-details
                    >
                      <template v-slot:label>
                        <span class="checkbox-label">
                          <v-icon size="18" class="mr-2" color="#285096">mdi-incognito</v-icon>
                          {{ $t('feedback-form.submit-anonymously') }}
                        </span>
                      </template>
                    </v-checkbox>
                  </div>
                </v-col>

                <!-- Feedback Message - Full Width -->
                <v-col cols="12">
                  <div class="form-group">
                    <label class="form-label">
                      <v-icon size="18" class="mr-2" color="#285096">mdi-message-text-outline</v-icon>
                      {{ $t('feedback-form.your-feedback') }}
                    </label>
                    <v-textarea
                      v-model="feedback.message"
                      variant="outlined"
                      rounded="lg"
                      auto-grow
                      rows="6"
                      class="form-textarea"
                      required
                    ></v-textarea>
                  </div>
                </v-col>

                <!-- Submit Button - Full Width -->
                <v-col cols="12">
                  <v-btn
                    :disabled="!canSubmit"
                    variant="flat"
                    color="#285096"
                    rounded="lg"
                    size="x-large"
                    block
                    class="text-none submit-button"
                    prepend-icon="mdi-send"
                    @click="submitFeedback"
                  >
                    {{ $t('feedback-form.submit') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import Swal from '@/services/ui/dialog'
import { getPrimaryColor } from '@/assets/Branding/branding.js'
import { useOAuthService } from '@/services/OAuthService'
import Loader from '@/components/general/Loader.vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { getToolbarLogoImage } from '@/assets/Branding/branding.js'

export default {
  name: 'FeedbackForm',
  components: {
    Loader,
  },

  props: {
    showAdminFeedbacks: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  setup() {
    const isAnonymous = ref(false)
    const OAuthService = useOAuthService()
    const showLoader = ref(false)
    const primaryColor = ref('')
    const router = useRouter()
    const route = useRoute()
    const logo = ref('')
    const { locale, t } = useI18n()

    const categories = ref([
      'All Categories',
      'UI/UX',
      'Performance',
      'Bug Report',
      'Feature Request',
      'Room Booking',
      'Building View',
      'Hardware Control',
      'Elevator Control',
      'News Center',
      'Motivation Center',
      'Chat/Communication',
      'Analytics',
      'Bookings Management',
      'Help Section',
      'Login/Logout',
      'Privacy & GDPR',
      'Language Switching',
      'Accessibility',
      'Security',
      'Notifications',
      'User Profiles',
      'Integration with Outlook',
      'Vouchers',
      'General Feedback',
      'Other',
    ])

    const feedback = ref({
      category: '',
      rating: 0,
      name: '',
      message: '',
    })

    const toggleAnonymous = () => {
      if (isAnonymous.value) {
        feedback.value.name = 'Anonymous'
      } else {
        feedback.value.name = ''
      }
    }

    const canSubmit = computed(() => {
      return (
        feedback.value.category !== '' &&
        feedback.value.rating > 0 &&
        (isAnonymous.value || feedback.value.name.trim() !== '') &&
        feedback.value.message.trim() !== ''
      )
    })

    const goBack = () => {
      window.history.back()
    }

    const submitFeedback = async () => {
      try {
        const formData = new FormData()
        formData.append('entry.1647756974', feedback.value.category)
        formData.append('entry.1134755108', feedback.value.rating)
        formData.append('entry.92267303', feedback.value.name)
        formData.append('entry.1394086581', feedback.value.message)

        await fetch('https://docs.google.com/forms/u/0/d/e/1FAIpQLSfvVcA4HxgHqOoOhIqRJuIxJ-dA6lMvBKTGzvGzIyc9DHY-xQ/formResponse', {
          method: 'POST',
          body: formData,
          mode: 'no-cors',
        })

        Swal.fire({
          icon: 'success',
          title: t('feedback-form.thanks'),
          text: t('feedback-form.submitted'),
          confirmButtonColor: '#285096',
        }).then(() => {
          feedback.value.category = ''
          feedback.value.rating = 0
          feedback.value.name = ''
          feedback.value.message = ''
          isAnonymous.value = false
        })
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: t('feedback-form.feedback-failed'),
          text: t('feedback-form.feedback-failed-description'),
          confirmButtonColor: '#DC3545',
        })
      }
    }

    onMounted(async () => {
      logo.value = await getToolbarLogoImage()
      primaryColor.value = await getPrimaryColor()
    })

    return {
      isAnonymous,
      primaryColor,
      categories,
      feedback,
      toggleAnonymous,
      canSubmit,
      submitFeedback,
      goBack,
      showLoader,
      logo,
    }
  },
}
</script>

<style scoped>
/* ===== CONTAINER ===== */
.feedback-view {
  min-height: var(--app-viewport-height, 100vh);
  background: linear-gradient(180deg, #EDF3FD 0%, #F8FAFC 50%, #FFFFFF 100%);
  padding: 12px;
}

/* ===== HEADER CARD ===== */
.header-card {
  background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(40, 80, 150, 0.3) !important;
  margin-bottom: 16px;
}

.header-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 1;
  pointer-events: none;
}

.back-btn {
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-4px);
}

.logo-wrapper {
  background: #FFFFFF;
  padding: 10px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.toolbar-logo {
  height: 40px;
  width: auto;
  /* Remove white filter - keep original logo colors */
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* ===== CONTENT CONTAINER ===== */
.content-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
}

/* ===== INFO CARD (LEFT COLUMN) ===== */
.info-card {
  background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%);
  border: 1px solid rgba(40, 80, 150, 0.1);
  box-shadow: 0 8px 32px rgba(40, 80, 150, 0.12) !important;
  height: 100%;
}

.feedback-icon-wrapper {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, rgba(40, 80, 150, 0.1) 0%, rgba(35, 165, 230, 0.1) 100%);
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(40, 80, 150, 0.15);
}

.info-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #001437;
  letter-spacing: -0.75px;
  line-height: 1.2;
}

.info-description {
  font-size: 0.9375rem;
  color: #4E5965;
  line-height: 1.6;
}

/* ===== FEEDBACK CARD (RIGHT COLUMN) ===== */
.feedback-card {
  background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%);
  border: 1px solid rgba(40, 80, 150, 0.1);
  box-shadow: 0 8px 32px rgba(40, 80, 150, 0.12) !important;
}

/* ===== FORM ELEMENTS ===== */
.feedback-form {
  width: 100%;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.checkbox-group {
  justify-content: flex-end;
}

.invisible {
  visibility: hidden;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #001437;
  display: flex;
  align-items: center;
}

.form-select,
.form-input,
.form-textarea {
  transition: all 0.3s ease;
}

.form-select :deep(.v-field),
.form-input :deep(.v-field),
.form-textarea :deep(.v-field) {
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(40, 80, 150, 0.2);
  box-shadow: 0 2px 8px rgba(40, 80, 150, 0.05);
}

.form-select:hover :deep(.v-field),
.form-input:hover :deep(.v-field),
.form-textarea:hover :deep(.v-field) {
  border-color: rgba(40, 80, 150, 0.4);
  box-shadow: 0 4px 12px rgba(40, 80, 150, 0.1);
}

.form-select :deep(.v-field--focused),
.form-input :deep(.v-field--focused),
.form-textarea :deep(.v-field--focused) {
  border-color: #285096;
  box-shadow: 0 4px 16px rgba(40, 80, 150, 0.2);
}

.form-input:disabled :deep(.v-field) {
  opacity: 0.5;
  background: #F5F5F5;
}

/* ===== RATING ===== */
.rating-wrapper {
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 8px 0;
}

.star-rating {
  display: flex;
  justify-content: flex-start;
}

.star-rating :deep(.v-rating__item) {
  padding: 4px;
}

/* ===== CHECKBOX ===== */
.anonymous-checkbox {
  margin-top: 8px;
}

.anonymous-checkbox :deep(.v-label) {
  font-size: 0.875rem;
  color: #001437;
}

.checkbox-label {
  display: flex;
  align-items: center;
  font-weight: 500;
}

/* ===== SUBMIT BUTTON ===== */
.submit-button {
  box-shadow: 0 8px 24px rgba(40, 80, 150, 0.25) !important;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: 0.5px;
  margin-top: 8px;
}

.submit-button:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(40, 80, 150, 0.35) !important;
}

.submit-button:disabled {
  opacity: 0.5;
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 960px) {
  .feedback-view {
    padding: 10px;
  }

  .header-title {
    font-size: 1.25rem;
  }

  .toolbar-logo {
    height: 36px;
  }

  .info-title {
    font-size: 1.375rem;
  }

  .feedback-icon-wrapper {
    width: 64px;
    height: 64px;
  }

  .content-container {
    padding: 0 8px !important;
  }
}

@media (max-width: 600px) {
  .feedback-view {
    padding: 8px;
  }

  .content-container {
    padding: 0 4px !important;
  }

  .logo-wrapper {
    padding: 8px;
  }

  .toolbar-logo {
    height: 32px;
  }

  .header-title {
    font-size: 1.125rem;
  }

  .feedback-card,
  .info-card {
    padding: 20px !important;
  }

  .info-title {
    font-size: 1.25rem;
  }

  .info-description {
    font-size: 0.875rem;
  }

  .feedback-icon-wrapper {
    width: 56px;
    height: 56px;
  }

  .submit-button {
    font-size: 0.9375rem;
  }
  .checkbox-group {
    justify-content: flex-start;
  }
}
</style>
